import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "@tamagui/lucide-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { ScrollView, Sheet, XStack, YStack } from "tamagui";
import { z } from "zod";

import {
  RXQuestionContent,
  RXQuestionContentReplySuggestionType as ReplyType,
} from "@/api/generated/models";
import {
  useSendLocationInput,
  useSendRxInput,
} from "@/api/mutations/conversation";
import {
  Button,
  Checkbox,
  ConstrainedView,
  Heading,
  Input,
  Logo,
  Paragraph,
  ProgressBar,
  Select,
  Skeleton,
  Spacer,
  Text,
} from "@/components";
import LoadingSpinner from "@/components/animated/LoadingSpinner";
import HeightWeightInput from "@/conversation/HeightWeightInput";
import { useCurrentConversationId } from "@/hooks/conversation";
import { useInfiniteMessages } from "@/hooks/useInfiniteMessages";
import { logger } from "@/sentry";
import { useRxQuestionnaireStore } from "@/store/rxQuestionnaire";

const CHOICE_REPLY_TYPES = new Set<ReplyType>([
  ReplyType.multiple_choice_multiple,
  ReplyType.multiple_choice_single,
  ReplyType.single_select_dropdown,
]);

const answerSchema = z
  .object({
    replyType: z.nativeEnum(ReplyType).optional(),
    selectedAnswers: z.array(z.string()),
    freeTextAnswer: z.string(),
    otherText: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.replyType && CHOICE_REPLY_TYPES.has(data.replyType)) {
      if (data.selectedAnswers.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["selectedAnswers"],
          message: "Please select an answer",
        });
        return;
      }
      if (data.selectedAnswers.includes("Other") && !data.otherText.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otherText"],
          message: "Please specify",
        });
      }
      return;
    }
    if (!data.freeTextAnswer.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["freeTextAnswer"],
        message: "Please enter an answer",
      });
    }
  });

type AnswerFormData = z.infer<typeof answerSchema>;

const EMPTY_ANSWER: AnswerFormData = {
  replyType: undefined,
  selectedAnswers: [],
  freeTextAnswer: "",
  otherText: "",
};

interface RxQuestionContentProps {
  conversationId: string;
}

const RxQuestionContent = ({ conversationId }: RxQuestionContentProps) => {
  const { data: messagesData, isLoading } = useInfiniteMessages(conversationId);
  const { close } = useRxQuestionnaireStore();
  const { sendRxInput } = useSendRxInput(conversationId);
  const { sendLocationInput } = useSendLocationInput(conversationId);

  const { control, handleSubmit, reset } = useForm<AnswerFormData>({
    defaultValues: EMPTY_ANSWER,
    resolver: zodResolver(answerSchema),
    shouldFocusError: true,
  });

  const {
    field: selectedAnswersField,
    fieldState: { error: selectedAnswersError },
  } = useController({ control, name: "selectedAnswers" });
  const {
    field: freeTextAnswerField,
    fieldState: { error: freeTextAnswerError },
  } = useController({ control, name: "freeTextAnswer" });
  const {
    field: otherTextField,
    fieldState: { error: otherTextError },
  } = useController({ control, name: "otherText" });

  const selectedAnswers = selectedAnswersField.value;
  const freeTextAnswer = freeTextAnswerField.value;
  const otherText = otherTextField.value;

  const [isWaitingForNext, setIsWaitingForNext] = useState(false);
  const [_isThinkingComplete, setIsThinkingComplete] = useState(false);
  const previousQuestionCountRef = useRef<number>(0);

  const { currentQuestion, questionCount, isRenewal, latestQuestionType } =
    useMemo(() => {
      const allMessages = messagesData?.allMessages || [];
      const rxQuestions = allMessages.filter(
        (msg) => msg.type === "rx_question" || msg.type === "location_question",
      );
      const count = rxQuestions.length;

      const rxConsentMessages = allMessages.filter(
        (msg) => msg.type === "rx_flow_consent",
      );
      const latestConsent = rxConsentMessages[rxConsentMessages.length - 1];
      const renewal = (latestConsent?.content as any)?.flow_type === "renewal";

      if (count === 0)
        return {
          currentQuestion: null,
          questionCount: 0,
          isRenewal: renewal,
          latestQuestionType: null,
        };

      const latestQuestion = rxQuestions[count - 1];
      const questionType = latestQuestion.type;
      const content = latestQuestion.content as RXQuestionContent;

      // Validate content structure (skip validation if survey is complete)
      if (
        !content ||
        typeof content !== "object" ||
        (!content.next_question && !content.screening_complete)
      ) {
        logger.error("Invalid RX question content", {
          category: "api",
          content,
        });
        return {
          currentQuestion: null,
          questionCount: count,
          isRenewal: renewal,
          latestQuestionType: questionType,
        };
      }

      return {
        currentQuestion: {
          question: content.next_question,
          replyType: content.reply_suggestion_type,
          options: content.reply_suggestions || [],
          hint: content.question_hint,
          screeningComplete: content.screening_complete || false,
        },
        questionCount: count,
        isRenewal: renewal,
        latestQuestionType: questionType,
      };
    }, [messagesData?.allMessages]);

  const MAX_QUESTIONS = isRenewal ? 15 : 9;
  // answeredQuestions shows how many have been completed (0-10)
  const answeredQuestions = Math.max(0, questionCount - 1);
  const progressValue = (answeredQuestions / MAX_QUESTIONS) * 100;

  const allMessages = messagesData?.allMessages || [];
  const hasAnyRxQuestions = allMessages.some(
    (msg) => msg.type === "rx_question",
  );
  const hasQuestionnaireEnded = allMessages.some(
    (msg) => msg.type === "rx_questionnaire_end",
  );

  const hasQuestionnaireStarted = allMessages.some(
    (msg) => msg.type === "rx_questionnaire_start",
  );

  const hasRxFlowConsent = allMessages.some(
    (msg) => msg.type === "rx_flow_consent",
  );

  // Safety check: close sheet if it's open but no RX flow exists
  // (can happen during hot refresh or navigation)
  // Only run after data has loaded to avoid closing during initial fetch
  useEffect(() => {
    if (
      !isLoading &&
      !hasRxFlowConsent &&
      !hasQuestionnaireStarted &&
      !hasAnyRxQuestions
    ) {
      close();
    }
  }, [
    isLoading,
    hasRxFlowConsent,
    hasQuestionnaireStarted,
    hasAnyRxQuestions,
    close,
  ]);

  useEffect(() => {
    if (
      currentQuestion?.question &&
      questionCount !== previousQuestionCountRef.current
    ) {
      setIsWaitingForNext(false);
      reset(
        { ...EMPTY_ANSWER, replyType: currentQuestion.replyType },
        { keepFieldsRef: true },
      );
      previousQuestionCountRef.current = questionCount;
    }
  }, [
    questionCount,
    currentQuestion?.question,
    currentQuestion?.replyType,
    reset,
  ]);

  // Auto-close after 3 seconds when survey is complete
  useEffect(() => {
    if ((!currentQuestion || hasQuestionnaireEnded) && hasAnyRxQuestions) {
      setIsThinkingComplete(true);
      const timer = setTimeout(() => {
        close();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [currentQuestion, hasQuestionnaireEnded, hasAnyRxQuestions, close]);

  if (!currentQuestion || hasQuestionnaireEnded) {
    return (
      <ConstrainedView paddingVertical="$300" gap="$300">
        <XStack justifyContent="space-between" alignItems="center">
          <Spacer size="$200" />
          <Logo />
          <Button.Wrapper
            round
            onPress={close}
            aria-label="Close survey"
            padding="$100"
            backgroundColor="$transparent"
          >
            <X size={20} />
          </Button.Wrapper>
        </XStack>

        <ProgressBar
          aria-label="Questionnaire progress"
          value={progressValue}
        />
        {hasAnyRxQuestions ? (
          <YStack gap="$200" justifyContent="center" alignItems="center">
            <LoadingSpinner size={48} />
            <Heading size="$md">Reviewing your answers...</Heading>
            <Paragraph color="$textSecondary" textAlign="center">
              It'll only take a moment...
            </Paragraph>
          </YStack>
        ) : (
          <>
            <YStack gap="$200">
              <Skeleton.Text lines={2} widths={["100%", "85%"]} />
            </YStack>
            <YStack gap="$100">
              <Skeleton height="$500" borderRadius="$200" />
              <Skeleton height="$500" borderRadius="$200" />
              <Skeleton height="$500" borderRadius="$200" />
              <Skeleton height="$500" borderRadius="$200" />
            </YStack>

            <Skeleton height="$500" borderRadius="$200" />
          </>
        )}
      </ConstrainedView>
    );
  }

  const sendInput = (message: string) => {
    if (latestQuestionType === "location_question") {
      sendLocationInput(message);
    } else {
      sendRxInput(message);
    }
  };

  const handleMultipleChoiceToggle = (answer: string) =>
    selectedAnswersField.onChange(
      selectedAnswers.includes(answer)
        ? selectedAnswers.filter((a) => a !== answer)
        : [...selectedAnswers, answer],
    );

  const handleSingleChoiceSelect = (answer: string) => {
    if (!conversationId) return;

    // If "Other" is selected, just store it and show the input
    if (answer === "Other") {
      selectedAnswersField.onChange([answer]);
      return;
    }

    setIsWaitingForNext(true);
    sendInput(answer);
  };

  const handleContinue = handleSubmit(
    ({ selectedAnswers, otherText, freeTextAnswer }) => {
      if (!conversationId) return;

      let answerText: string;
      switch (currentQuestion.replyType) {
        case ReplyType.multiple_choice_multiple:
          answerText = selectedAnswers
            .map((a) => (a === "Other" && otherText ? otherText : a))
            .join(", ");
          break;
        case ReplyType.multiple_choice_single:
          answerText =
            selectedAnswers[0] === "Other" && otherText
              ? otherText
              : selectedAnswers[0];
          break;
        case ReplyType.single_select_dropdown:
          answerText = selectedAnswers[0];
          break;
        default:
          answerText = freeTextAnswer;
      }

      setIsWaitingForNext(true);
      sendInput(answerText);
    },
  );

  if (isWaitingForNext) {
    return (
      <ConstrainedView paddingVertical="$300" gap="$300">
        <XStack justifyContent="space-between" alignItems="center">
          <Spacer size="$200" />
          <Logo />
          <Button.Wrapper
            round
            onPress={close}
            aria-label="Close survey"
            padding="$100"
            backgroundColor="$transparent"
          >
            <X size={20} />
          </Button.Wrapper>
        </XStack>

        <ProgressBar
          aria-label="Questionnaire progress"
          value={progressValue}
        />

        <YStack gap="$200">
          <Skeleton.Text lines={2} widths={["100%", "85%"]} />
        </YStack>

        <YStack gap="$100">
          <Skeleton height="$500" borderRadius="$200" />
          <Skeleton height="$500" borderRadius="$200" />
          <Skeleton height="$500" borderRadius="$200" />
          <Skeleton height="$500" borderRadius="$200" />
        </YStack>

        <Skeleton height="$500" borderRadius="$200" />
      </ConstrainedView>
    );
  }

  return (
    <ConstrainedView paddingVertical="$300" gap="$300">
      <XStack justifyContent="space-between" alignItems="center">
        <Spacer size="$200" />
        <Logo />
        <Button.Wrapper
          round
          onPress={close}
          aria-label="Close survey"
          padding="$100"
          backgroundColor="$transparent"
        >
          <X size={20} />
        </Button.Wrapper>
      </XStack>

      <ProgressBar aria-label="Questionnaire progress" value={progressValue} />

      <YStack gap="$200">
        <Heading size="$lg">{currentQuestion.question}</Heading>
        {currentQuestion.replyType === ReplyType.multiple_choice_multiple && (
          <Text size="$sm" color="$textSecondary">
            Choose all that apply.
          </Text>
        )}
      </YStack>

      {(() => {
        switch (currentQuestion.replyType) {
          case ReplyType.height_weight:
            return (
              <HeightWeightInput
                onSubmit={({ displayText, heightIn, weightLb }) => {
                  setIsWaitingForNext(true);
                  sendRxInput(displayText, {
                    height_in: heightIn,
                    weight_lb: weightLb,
                  });
                }}
              />
            );
          case ReplyType.single_select_dropdown:
            return (
              <YStack gap="$100">
                <Select
                  ref={selectedAnswersField.ref}
                  value={selectedAnswers[0] || ""}
                  onValueChange={(val: string) =>
                    selectedAnswersField.onChange([val])
                  }
                  items={currentQuestion.options.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  placeholder="Select an option..."
                  errorText={selectedAnswersError?.message}
                />
              </YStack>
            );
          case ReplyType.multiple_choice_single:
            return (
              <YStack gap="$100">
                {currentQuestion.options.map((option: string, index) => (
                  <Button
                    key={option}
                    ref={index === 0 ? selectedAnswersField.ref : undefined}
                    mode="surveyOption"
                    onPress={() => handleSingleChoiceSelect(option)}
                    dataTrackingId="rx-questionnaire-option"
                  >
                    {option}
                  </Button>
                ))}
                {selectedAnswersError && (
                  <Text size="$xs" color="$red">
                    {selectedAnswersError.message}
                  </Text>
                )}
                {selectedAnswers.includes("Other") && (
                  <Input.Controlled
                    ref={otherTextField.ref}
                    value={otherText}
                    onChangeText={otherTextField.onChange}
                    placeholder="Please specify..."
                    aria-label="Other option text"
                    errorText={otherTextError?.message}
                  />
                )}
              </YStack>
            );
          case ReplyType.multiple_choice_multiple:
            return (
              <YStack gap="$100">
                {currentQuestion.options.map((option: string, index) => (
                  <Checkbox
                    key={option}
                    ref={index === 0 ? selectedAnswersField.ref : undefined}
                    label={option}
                    wrapped
                    checked={selectedAnswers.includes(option)}
                    onCheckedChange={() => handleMultipleChoiceToggle(option)}
                  />
                ))}
                {selectedAnswersError && (
                  <Text size="$xs" color="$red">
                    {selectedAnswersError.message}
                  </Text>
                )}
                {selectedAnswers.includes("Other") && (
                  <Input.Controlled
                    ref={otherTextField.ref}
                    value={otherText}
                    onChangeText={otherTextField.onChange}
                    placeholder="Please specify..."
                    aria-label="Other option text"
                    errorText={otherTextError?.message}
                  />
                )}
              </YStack>
            );
          default:
            return (
              <YStack gap="$100">
                <Input.Controlled
                  ref={freeTextAnswerField.ref}
                  value={freeTextAnswer}
                  onChangeText={freeTextAnswerField.onChange}
                  placeholder="Type your answer here..."
                  aria-label={currentQuestion.question || "Answer"}
                  multiline
                  numberOfLines={4}
                  errorText={freeTextAnswerError?.message}
                />
              </YStack>
            );
        }
      })()}

      {/* TODO: NORI-1131 Hiding this until product wants us to turn it back on */}
      {/* {currentQuestion.hint && <QuestionHint hint={currentQuestion.hint} />} */}

      {currentQuestion.replyType !== ReplyType.height_weight &&
        (currentQuestion.replyType !== ReplyType.multiple_choice_single ||
          selectedAnswers.includes("Other")) && (
          <Button mode="primary" onPress={handleContinue}>
            Continue
          </Button>
        )}
    </ConstrainedView>
  );
};

export default function RxQuestionnaire() {
  const { isOpen, close } = useRxQuestionnaireStore();
  const conversationId = useCurrentConversationId();

  if (!conversationId) return null;

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open: boolean) => !open && close()}
      snapPoints={[98]}
      transition="standard"
      modal={true}
      disableDrag
    >
      <Sheet.Overlay
        backgroundColor="$fillOverlay"
        backdropFilter="blur(8px)"
        transition="standard"
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
      />
      <Sheet.Frame padding="$0" flex={1}>
        <ScrollView>
          <RxQuestionContent conversationId={conversationId} />
        </ScrollView>
      </Sheet.Frame>
    </Sheet>
  );
}
